/**
 * The permission catalogue, mirrored from the backend.
 *
 * ---------------------------------------------------------------------------
 * THE BACKEND IS THE SOURCE OF TRUTH
 *
 * A user's permissions come from `GET /api/admin/me`, computed server-side from
 * the user's CURRENT role (UT system/Authorization/RolePermissions.cs). This
 * file does not — and must not — derive permissions from a role. It exists only
 * so that route declarations, navigation entries and `<Can>` checks are
 * type-checked against real permission names.
 *
 * Keep it in step with UT system/Authorization/Permissions.cs. A permission the
 * backend sends that is missing here is ignored (it can only hide UI, never
 * grant anything); a name here the backend never sends simply never matches.
 *
 * ---------------------------------------------------------------------------
 * THIS IS NOT A SECURITY BOUNDARY
 *
 * Everything here runs in the browser. It decides what is *shown*, never what
 * is *allowed* — the backend enforces authorization on every request.
 * ------------------------------------------------------------------------- */

export const PERMISSIONS = [
  // Admin application access
  'admin.access',

  // Screens that exist today
  'dashboard.read',
  'orders.read',
  'orders.details.read',
  // An order's step-by-step pricing calculation, provider cost included.
  'orders.calculation.read',
  'orders.codes.reveal',
  'orders.codes.resend',
  'customers.read',
  'customers.limits.manage',
  'customers.manage',
  'complaints.manage',
  'complaints.read',
  'refunds.request',
  'refunds.approve',
  'refunds.approve.senior',
  'refunds.approve.own',
  'refunds.payout.record',
  'reconciliation.read',
  'reconciliation.resolve',
  'system.health.read',
  'catalogue.read',
  'catalogue.manage',
  'catalogue.cost.read',
  'catalogue.availability.manage',

  // Reporting foundation. Delivered by /me, but no route, navigation entry or
  // page uses them yet — each reporting feature introduces its own screens
  // when it is built.
  'reports.transactions.read',
  'transactions.details.read',
  'transactions.history.read',
  'reports.operations.read',
  'reports.financial.read',
  'reports.pricing.read',
  'pricing.snapshot.read',
  'reports.providers.read',
  'reports.catalogue.read',
  'reports.partner.read',
  'reports.audit.read',
  'reports.export',

  // Configuration
  'countries.manage',
  'pricing.read',
  'pricing.config.manage',
  'pricing.config.approve',
  'pricing.config.approve.own',
  'pricing.calculation.read',
  'fx.override.request',
  'providers.read',
  'providers.manage',
  'providers.balance.approve',
  'approvals.read',
  'users.manage',

  // Marketing & content
  'newsletter.read',
  'newsletter.manage',
  // Loyalty points (Phase 1)
  'loyalty.read',
  'loyalty.manage',
  'loyalty.adjust',
  // Coupons
  'coupons.read',
  'coupons.manage',
  // Marketing campaigns (tracker links) and agencies. Revenue also needs reports.financial.read,
  // the campaign customer list customers.read.
  'campaigns.read',
  'campaigns.manage',

  // Financial settlements: channel and partner statements
  'settlements.read',
  'settlements.issue',
  'settlements.payment.record',
  'settlements.void',
  'settlements.partners.manage',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

const KNOWN_PERMISSIONS: ReadonlySet<string> = new Set(PERMISSIONS);

export function isPermission(value: unknown): value is Permission {
  return typeof value === 'string' && KNOWN_PERMISSIONS.has(value);
}

/**
 * Friendly labels for the backend's `Users.Role` values.
 *
 * Display only. The staff roles carry a `Dc` prefix in the database so they do
 * not collide with role names the Next.js customer app already recognises.
 * Never branch on a role — check a permission instead.
 */
const ROLE_LABELS: Readonly<Record<string, string>> = {
  Admin: 'Admin',
  DcCustomerCare: 'Customer Care / Operations',
  DcFinance: 'Finance',
  DcCatalogueManager: 'Product / Catalogue Manager',
  DcPartner: 'Partner / Aggregator',
};

export function roleLabel(role: string | null | undefined): string {
  if (!role) return 'Unknown role';
  return ROLE_LABELS[role] ?? role;
}
